import React from 'react';
import { useNavigate } from 'react-router-dom';
import { GenericView, SearchInput, ActionButtons } from '../../../components/common/table';

/**
 * Shared "List Data" table for sales achievement (per salesperson / invoice creator).
 */
const SalesAchievementListTable = ({
    salesData,
    metrics,
    filters,
    searchQuery,
    setSearchQuery,
    isLoading,
    handleExportExcel,
    formatCurrencyValue,
    uniqueId = 'sales-achievement-list',
    searchPlaceholder = 'Search salesperson...',
    incentiveButton = null,
    countColumnLabel = 'TOTAL INVOICES',
    disableInvoiceNavigation = false,
    onCountClick = null
}) => {
    const navigate = useNavigate();

    const navigateToInvoice = (salespersonData = null, status = null) => {
        // Use the exact bill-date range the backend resolved for this period/metrics,
        // so the invoice list matches the counts shown on the table.
        const billDateFrom = metrics?.billDateFrom || filters.startDate;
        const billDateTo = metrics?.billDateTo || filters.endDate;

        const params = new URLSearchParams();
        if (billDateFrom) params.append('billDateFrom', billDateFrom);
        if (billDateTo) params.append('billDateTo', billDateTo);

        if (salespersonData && salespersonData.id && salespersonData.name !== 'GRAND TOTAL') {
            params.append('assignedTo', salespersonData.id);
        }

        const serviceIds = Array.isArray(filters.service) ? filters.service : (filters.service ? [filters.service] : []);
        if (serviceIds.length > 0 && serviceIds[0] != null && serviceIds[0] !== '') {
            params.append('serviceId', String(serviceIds[0]));
        }

        const departmentIds = Array.isArray(filters.departmentId) ? filters.departmentId : (filters.departmentId ? [filters.departmentId] : []);
        if (departmentIds.length > 0 && departmentIds[0] != null && departmentIds[0] !== '') {
            params.append('departmentId', String(departmentIds[0]));
        }

        if (status) {
            if (Array.isArray(status)) {
                status.forEach(s => params.append('status', s));
            } else {
                params.append('status', status);
            }
        } else {
            params.append('status', 'SENT');
            params.append('status', 'PAID');
            params.append('status', 'FINAL');
            params.append('status', 'PARTIAL');
        }

        navigate(`/invoice?${params.toString()}`);
    };

    const nonTotalRows = salesData.filter(item => item.name !== 'GRAND TOTAL');
    const grandTotalRow = {
        name: 'GRAND TOTAL',
        invoiceCount: metrics.invoiceCount || nonTotalRows.reduce((sum, r) => sum + (r.invoiceCount || 0), 0),
        subtotal: nonTotalRows.reduce((sum, r) => sum + (r.subtotal || 0), 0),
        ppn: nonTotalRows.reduce((sum, r) => sum + (r.ppn || 0), 0),
        totalValue: nonTotalRows.reduce((sum, r) => sum + (r.totalValue || 0), 0),
        contribution: nonTotalRows.reduce((sum, r) => sum + (r.contribution || 0), 0),
        achievementOfTarget: metrics.achievement || 0,
        targetForPeriod: null
    };

    const filteredNonTotal = nonTotalRows.filter(item => {
        if (!searchQuery) return true;
        const query = searchQuery.toLowerCase();
        return item.name?.toLowerCase().includes(query);
    });
    const filteredData = searchQuery ? filteredNonTotal : [...filteredNonTotal, grandTotalRow];

    const columns = [
        {
            key: 'name',
            label: 'SALES PERSON',
            render: (name) => {
                const isTotal = name === 'GRAND TOTAL';
                return (
                    <div className={isTotal ? 'fw-bold' : ''}>{name}</div>
                );
            },
            sortable: true
        },
        {
            key: 'invoiceCount',
            label: countColumnLabel,
            render: (count, row) => {
                const isTotal = row.name === 'GRAND TOTAL';
                const invoiceCount = count || 0;

                if (onCountClick) {
                    if (isTotal) {
                        return <div className="fw-bold">{invoiceCount}</div>;
                    }
                    return (
                        <div
                            style={{ cursor: 'pointer', color: '#0dcaf0', transition: 'opacity 0.2s' }}
                            onClick={() => onCountClick(row)}
                            onMouseEnter={(e) => { e.target.style.opacity = '0.7'; }}
                            onMouseLeave={(e) => { e.target.style.opacity = '1'; }}
                            title={`Click to view projects for ${row.name}`}
                        >
                            {invoiceCount}
                        </div>
                    );
                }

                if (disableInvoiceNavigation) {
                    return <div className={isTotal ? 'fw-bold' : ''}>{invoiceCount}</div>;
                }

                if (isTotal) {
                    return (
                        <div
                            className="fw-bold"
                            style={{ cursor: 'pointer', color: '#0dcaf0', transition: 'opacity 0.2s' }}
                            onClick={() => navigateToInvoice()}
                            onMouseEnter={(e) => { e.target.style.opacity = '0.7'; }}
                            onMouseLeave={(e) => { e.target.style.opacity = '1'; }}
                            title="Click to view all invoices"
                        >
                            {invoiceCount}
                        </div>
                    );
                }

                return (
                    <div
                        style={{ cursor: 'pointer', color: '#0dcaf0', transition: 'opacity 0.2s' }}
                        onClick={() => navigateToInvoice(row)}
                        onMouseEnter={(e) => { e.target.style.opacity = '0.7'; }}
                        onMouseLeave={(e) => { e.target.style.opacity = '1'; }}
                        title={`Click to view invoices for ${row.name}`}
                    >
                        {invoiceCount}
                    </div>
                );
            },
            sortable: true
        },
        {
            key: 'subtotal',
            label: 'SUB TOTAL',
            render: (value, row) => {
                const isTotal = row.name === 'GRAND TOTAL';
                return (
                    <div className={isTotal ? 'fw-bold' : ''}>Rp {formatCurrencyValue(value || 0)}</div>
                );
            },
            sortable: true
        },
        {
            key: 'ppn',
            label: 'PPN',
            render: (value, row) => {
                const isTotal = row.name === 'GRAND TOTAL';
                return (
                    <div className={isTotal ? 'fw-bold' : ''}>Rp {formatCurrencyValue(value || 0)}</div>
                );
            },
            sortable: true
        },
        {
            key: 'totalValue',
            label: 'TOTAL VALUE (IDR)',
            render: (value) => (
                <div className="fw-bold">Rp {formatCurrencyValue(value || 0)}</div>
            ),
            sortable: true
        },
        {
            key: 'achievementOfTarget',
            label: 'ACHIEVEMENT % (of target)',
            render: (value, row) => {
                const isTotal = row.name === 'GRAND TOTAL';
                if (isTotal) {
                    return <div className="text-muted"></div>;
                }
                const achievementOfTarget = row.achievementOfTarget;
                const targetForPeriod = row.targetForPeriod;
                if (targetForPeriod == null || targetForPeriod === 0) {
                    return <div className="text-muted">—</div>;
                }
                const pct = achievementOfTarget != null ? Number(achievementOfTarget).toFixed(1) : '0.0';
                const isOver = achievementOfTarget >= 100;
                const periodLabel = metrics.targetPeriodLabel || metrics.targetYear || 'period';
                return (
                    <div title={`${pct}% of target (Rp ${formatCurrencyValue(targetForPeriod)}) for ${periodLabel}`}>
                        <span className={isOver ? 'text-success fw-medium' : ''}>{pct}%</span>
                    </div>
                );
            },
            sortable: true
        },
        {
            key: 'contribution',
            label: 'CONTRIBUTION (to total target)',
            render: (value, row) => {
                const isTotal = row.name === 'GRAND TOTAL';
                const contribution = row.contribution;
                return (
                    <div className={isTotal ? 'fw-bold' : ''} title="Share of total department target for the period">
                        {contribution != null ? Number(contribution).toFixed(1) : '0.0'}%
                    </div>
                );
            },
            sortable: true
        }
    ];

    return (
        <div className="card">
            <div className="card-header">
                <ul className="nav nav-tabs card-header-tabs" data-bs-toggle="tabs" role="tablist">
                    <li className="nav-item-tab" role="presentation">
                        <a
                            href={`#tabs-list-data-${uniqueId}`}
                            className="nav-link active"
                            data-bs-toggle="tab"
                            aria-selected="true"
                            role="tab"
                        >
                            List Data
                        </a>
                    </li>
                </ul>
            </div>
            <div className="card-body">
                <div className="tab-content">
                    <div className="tab-pane active show" id={`tabs-list-data-${uniqueId}`} role="tabpanel">
                        <GenericView
                            data={filteredData}
                            columns={columns}
                            isLoading={isLoading}
                            emptyStateMessage="No sales data found"
                            searchComponent={
                                <SearchInput
                                    value={searchQuery}
                                    onChange={setSearchQuery}
                                    placeholder={searchPlaceholder}
                                />
                            }
                            actionButtons={
                                <div className="d-flex gap-2 align-items-center flex-wrap">
                                    {incentiveButton}
                                    <ActionButtons
                                        onExportExcel={handleExportExcel}
                                        uniqueId={uniqueId}
                                    />
                                </div>
                            }
                            rowClassName={(row) => {
                                return row.name === 'GRAND TOTAL' ? 'table-active fw-bold' : '';
                            }}
                            isRowSelectable={(row) => {
                                return row.name !== 'GRAND TOTAL';
                            }}
                            showPagination={false}
                        />
                    </div>
                </div>
            </div>
        </div>
    );
};

export default SalesAchievementListTable;
